import { useCallback, useState } from 'react';
import type {
  AuthFlowOperations,
  ConfirmPasswordResetPayload,
  ConfirmPasswordResetResult,
  ConfirmSignUpPayload,
  ConfirmSignUpResult,
  FederatedSignInPayload,
  PasswordResetPayload,
  PasswordResetResult,
  ResendSignUpCodePayload,
  ResendSignUpCodeResult,
  SignInPayload,
  SignInResult,
  SignUpPayload,
  SignUpResult,
} from './types.js';

export type UseAuthFlowControllerOptions = {
  operations: AuthFlowOperations;
  onAuthSuccess?: () => void;
};

export type UseAuthFlowControllerResult = {
  isSubmitting: boolean;
  errorMessage: string | null;
  infoMessage: string | null;
  clearMessages: () => void;
  submitSignIn: (payload: SignInPayload) => Promise<SignInResult | null>;
  submitSignUp: (payload: SignUpPayload) => Promise<SignUpResult | null>;
  submitPasswordReset: (payload: PasswordResetPayload) => Promise<PasswordResetResult | null>;
  submitConfirmPasswordReset: (
    payload: ConfirmPasswordResetPayload
  ) => Promise<ConfirmPasswordResetResult | null>;
  submitConfirmSignUp: (payload: ConfirmSignUpPayload) => Promise<ConfirmSignUpResult | null>;
  submitResendSignUpCode: (
    payload: ResendSignUpCodePayload
  ) => Promise<ResendSignUpCodeResult | null>;
  submitFederatedSignIn: (payload: FederatedSignInPayload) => Promise<boolean>;
};

const extractErrorMessage = (error: unknown): string => {
  if (typeof error === 'string' && error.trim()) {
    return error;
  }
  if (error && typeof error === 'object') {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
    const name = (error as { name?: unknown }).name;
    if (typeof name === 'string' && name.trim()) {
      return name;
    }
  }
  return 'Unable to complete authentication. Please try again.';
};

const resolveSignInNextStepMessage = (step?: string): string => {
  if (step === 'CONFIRM_SIGN_UP') {
    return 'Please verify your account from the email code, then sign in again.';
  }
  if (step === 'RESET_PASSWORD') {
    return 'This account requires a password reset before sign-in can continue.';
  }
  return 'Additional sign-in verification is required for this account.';
};

const resolveSignUpMessage = (result: SignUpResult): string => {
  if (result.message) {
    return result.message;
  }
  if (result.status === 'confirm_sign_up') {
    return 'Account created. Check your email for a verification code, then sign in.';
  }
  if (result.status === 'next_step') {
    return 'Account created. Additional verification is required before sign-in.';
  }
  return 'Account created. You can sign in now.';
};

const resolvePasswordResetMessage = (result: PasswordResetResult): string =>
  result.destination
    ? `Password reset code sent to ${result.destination}.`
    : 'Password reset code sent. Check your email for the code.';

const resolveConfirmPasswordResetMessage = (result: ConfirmPasswordResetResult): string =>
  result.message || 'Password updated. Sign in with your new password.';

const resolveConfirmSignUpMessage = (result: ConfirmSignUpResult): string =>
  result.message || 'Account verified. Sign in now.';

const resolveResendSignUpCodeMessage = (result: ResendSignUpCodeResult): string =>
  result.destination
    ? `Verification code resent to ${result.destination}.`
    : 'Verification code resent.';

export const useAuthFlowController = ({
  operations,
  onAuthSuccess,
}: UseAuthFlowControllerOptions): UseAuthFlowControllerResult => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  const clearMessages = useCallback(() => {
    setErrorMessage(null);
    setInfoMessage(null);
  }, []);

  const submitSignIn = useCallback(
    async (payload: SignInPayload): Promise<SignInResult | null> => {
      if (isSubmitting) {
        return null;
      }
      clearMessages();
      setIsSubmitting(true);
      try {
        const result = await operations.signIn(payload);
        if (result.status === 'authenticated') {
          onAuthSuccess?.();
        } else {
          setInfoMessage(resolveSignInNextStepMessage(result.nextStep));
        }
        return result;
      } catch (error) {
        setErrorMessage(extractErrorMessage(error));
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [clearMessages, isSubmitting, onAuthSuccess, operations]
  );

  const submitSignUp = useCallback(
    async (payload: SignUpPayload): Promise<SignUpResult | null> => {
      if (isSubmitting) {
        return null;
      }
      clearMessages();
      setIsSubmitting(true);
      try {
        const result = await operations.signUp(payload);
        setInfoMessage(resolveSignUpMessage(result));
        return result;
      } catch (error) {
        setErrorMessage(extractErrorMessage(error));
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [clearMessages, isSubmitting, operations]
  );

  const submitPasswordReset = useCallback(
    async (payload: PasswordResetPayload): Promise<PasswordResetResult | null> => {
      if (isSubmitting) {
        return null;
      }
      clearMessages();
      setIsSubmitting(true);
      try {
        const result = await operations.resetPassword(payload);
        setInfoMessage(resolvePasswordResetMessage(result));
        return result;
      } catch (error) {
        setErrorMessage(extractErrorMessage(error));
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [clearMessages, isSubmitting, operations]
  );

  const submitConfirmPasswordReset = useCallback(
    async (
      payload: ConfirmPasswordResetPayload
    ): Promise<ConfirmPasswordResetResult | null> => {
      if (isSubmitting) {
        return null;
      }
      clearMessages();
      setIsSubmitting(true);
      try {
        const result = await operations.confirmPasswordReset(payload);
        setInfoMessage(resolveConfirmPasswordResetMessage(result));
        return result;
      } catch (error) {
        setErrorMessage(extractErrorMessage(error));
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [clearMessages, isSubmitting, operations]
  );

  const submitConfirmSignUp = useCallback(
    async (payload: ConfirmSignUpPayload): Promise<ConfirmSignUpResult | null> => {
      if (isSubmitting) {
        return null;
      }
      clearMessages();
      setIsSubmitting(true);
      try {
        const result = await operations.confirmSignUp(payload);
        setInfoMessage(resolveConfirmSignUpMessage(result));
        return result;
      } catch (error) {
        setErrorMessage(extractErrorMessage(error));
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [clearMessages, isSubmitting, operations]
  );

  const submitResendSignUpCode = useCallback(
    async (payload: ResendSignUpCodePayload): Promise<ResendSignUpCodeResult | null> => {
      if (isSubmitting) {
        return null;
      }
      clearMessages();
      setIsSubmitting(true);
      try {
        const result = await operations.resendSignUpCode(payload);
        setInfoMessage(resolveResendSignUpCodeMessage(result));
        return result;
      } catch (error) {
        setErrorMessage(extractErrorMessage(error));
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [clearMessages, isSubmitting, operations]
  );

  const submitFederatedSignIn = useCallback(
    async (payload: FederatedSignInPayload): Promise<boolean> => {
      if (isSubmitting) {
        return false;
      }
      clearMessages();
      setIsSubmitting(true);
      try {
        await operations.federatedSignIn(payload);
        return true;
      } catch (error) {
        setErrorMessage(extractErrorMessage(error));
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [clearMessages, isSubmitting, operations]
  );

  return {
    isSubmitting,
    errorMessage,
    infoMessage,
    clearMessages,
    submitSignIn,
    submitSignUp,
    submitPasswordReset,
    submitConfirmPasswordReset,
    submitConfirmSignUp,
    submitResendSignUpCode,
    submitFederatedSignIn,
  };
};
